import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./AccessMode.css";
import PalletLogo from "../../../../../../assets/images/Pallet-logo-blue-transparent (2).png";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import { useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { logoutSession } from "../../../../../../config/Services";
import { getMobilePos } from "../../redux/Reducers/paginationReducer";
import { emit } from "react-native-react-bridge/lib/web";
import CircularProgress from "@mui/material/CircularProgress";
import useKdsAvailability from "../../../hooks/useKdsAvailability";

const AccessMode = () => {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(0);
  const isMobilePos = useSelector(getMobilePos);
  const { loading: isPreparingKds, enabled: isKdsEnabled, error: kdsCheckFailed } = useKdsAvailability();

  const handleLogout = () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    const payload = {
      metaData: {
        os: navigator.platform,
        latitude: "",
        browser: window.navigator.userAgent,
        city: "",
        country: "",
        postCode: "",
        longitude: "",
        device: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(
          navigator.userAgent
        )
          ? "This device is Mobile"
          : /Windows/i.test(navigator.userAgent)
          ? "This device is Desktop"
          : "This device is tablet",
      },
    };

    logoutSession(payload)
      .then((res) => {
        showSnackbar("Successfully Logged out", "success");
        isMobilePos && emit({ type: "localData", data: null });
        Object.keys(localStorage).forEach((key) => {
          if (key !== "printerConfig") {
            localStorage.removeItem(key);
          }
        });
        navigate("/");
        setIsLoggingOut(false);
      })
      .catch((err) => {
        showSnackbar(err.message || "Logout failed", "error");
        isMobilePos && emit({ type: "localData", data: null });
        Object.keys(localStorage).forEach((key) => {
          if (key !== "printerConfig") {
            localStorage.removeItem(key);
          }
        });
        setIsLoggingOut(false);
      });
  };
  const retailType = localStorage.getItem("retailType");

  const options = [{ id: "POS" }, ...(retailType === "RESTAURANT" ? [{ id: "KDS" }] : [])];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIdx((prev) => Math.min(prev + 1, options.length - 1));
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIdx((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        onSelectCard(options[focusedIdx].id);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusedIdx, options?.length, isPreparingKds, isKdsEnabled, isLoggingOut]);

  const onSelectCard = (selectedCard) => {
    if (isLoggingOut) return;
    if (selectedCard === "KDS") {
      if (isPreparingKds || !isKdsEnabled) return;
      navigate("/products/kitchen-display", { state: { fromAccessMode: true } });
      return;
    }

    navigate("/session-page/start-day");
  };

  return (
    <>
      {isLoggingOut && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            cursor: "not-allowed",
          }}
        />
      )}
      <div className="am-root" style={{ opacity: isLoggingOut ? 0.8 : 1 }}>
        <div className="am-header-card">
          <header className="am-header">
            <div className="am-header-left">
              <button
                className="am-back-btn"
                data-testid="button-access-mode-back"
                onClick={() => navigate("/products/location")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m12 19-7-7 7-7" />
                  <path d="M19 12H5" />
                </svg>
              </button>
              <img
                alt="Pallet logo"
                className="am-logo"
                data-testid="img-access-mode-logo"
                src={PalletLogo}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div className="am-step-badge">Step 2 of 4</div>
              <div
                onClick={handleLogout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: isLoggingOut ? "not-allowed" : "pointer",
                  color: "#e53e3e",
                }}
                title="Logout"
              >
                {isLoggingOut ? (
                  <ArrowPathIcon className="spinner" style={{ width: 20, height: 20 }} />
                ) : (
                  <LogoutIcon sx={{ fontSize: "1.25rem" }} />
                )}
                <span style={{ fontSize: "0.875rem", fontWeight: 600 }}>Logout</span>
              </div>
            </div>
          </header>
        </div>{/* end am-header-card */}
        {/* Main */}
        <main className="am-main">
            <section className="am-card">
              {/* Card heading */}
              <div className="am-card-heading">
                <p className="am-eyebrow">Access Mode</p>
                <h1 className="am-title" data-testid="text-access-mode-title">
                  What are you accessing today?
                </h1>
                <p className="am-subtitle">
                  Select the system you want to launch for this session.
                </p>
              </div>

              {/* Option grid */}
              <div className="am-options-grid">
                {/* POS */}
                <button
                  className={`am-option-btn am-option-pos${
                    focusedIdx === 0 ? " am-kb-focused" : ""
                  }`}
                  data-testid="button-access-mode-pos"
                  onClick={() => {
                    setFocusedIdx(0);
                    onSelectCard("POS");
                  }}
                >
                  <div className="am-option-icon am-option-icon--pos">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect width="20" height="14" x="2" y="3" rx="2" />
                      <line x1="8" x2="16" y1="21" y2="21" />
                      <line x1="12" x2="12" y1="17" y2="21" />
                    </svg>
                  </div>
                  <div className="am-option-text">
                    <h3 className="am-option-title">Point of Sale (POS)</h3>
                    <p className="am-option-desc">
                      Access billing, tables, order management, and counter operations.
                    </p>
                  </div>
                </button>
                {/* KDS */}
                {retailType === "RESTAURANT" && (
                  <button
                    className={`am-option-btn am-option-kds${
                      focusedIdx === 1 && isKdsEnabled ? " am-kb-focused" : ""
                    }`}
                    data-testid="button-access-mode-kds"
                    disabled={isPreparingKds || !isKdsEnabled}
                    aria-busy={isPreparingKds}
                    onClick={() => {
                      setFocusedIdx(1);
                      onSelectCard("KDS");
                    }}
                  >
                    <div className="am-option-icon am-option-icon--kds">
                      {isPreparingKds ? <CircularProgress size={28} color="inherit" /> : <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M3 5h18" />
                        <path d="M3 12h18" />
                        <path d="M3 19h18" />
                      </svg>}
                    </div>
                    <div className="am-option-text">
                      <h3 className="am-option-title">Kitchen Display (KDS)</h3>
                      <p className="am-option-desc">
                        {isPreparingKds
                          ? "Preparing KDS"
                          : kdsCheckFailed
                          ? "Unable to prepare KDS. Please reopen this page to try again."
                          : !isKdsEnabled
                          ? "KDS is disabled for this location."
                          : "View the cooking queue, mark orders ready, and manage kitchen prep."}
                      </p>
                    </div>
                  </button>
                )}
              </div>

              {/* Footer note */}
              <div className="am-note">
                <p>
                  Selecting KDS takes you straight to the kitchen display — no further setup needed.
                </p>
              </div>
            </section>
          </main>
      </div>
    </>
  );
};

export default AccessMode;
